import React from 'react';
import "../Login.css"

import { Form, Field, FormElement, FieldWrapper } from '@progress/kendo-react-form';
import { Input } from '@progress/kendo-react-inputs';
import { Card, CardBody, CardTitle } from '@progress/kendo-react-layout';
import { EmailInput, FormInput, FormMaskedTextBox } from "../../../Utils/form-components"
import { emailValidator, passwordValidationMessage, confirmPasswordValidationMessage, requiredValidator, firstNameValidator, lastNameValidator } from "../../../Utils/validators"
import { ChunkProgressBar } from '@progress/kendo-react-progressbars';
import { BIGrey, BIGreen, BIRed, BIYellow, BIBlue } from "../../../Utils/buildIdColors";
import { FaCheck, FaTimes } from "react-icons/fa";
import { sha256 } from 'crypto-hash';
import { Button } from '@progress/kendo-react-buttons';

import { Stepper } from '@progress/kendo-react-layout';
import { lockIcon, trackChangesIcon, userIcon } from '@progress/kendo-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { createAccountAdsClients, loginAdsClients } from '../../../Api/LoginApi';

export default function SignUp(props) {
    const location = useLocation();

    const [isVisible1, setIsVisible1] = React.useState(false);
    const [isVisible2, setIsVisible2] = React.useState(false);
    const [isCreateDisabled, setIsCreateDisabled] = React.useState(true);

    //fields
    const [password, setPassword] = React.useState("");
    const [retypePassword, setRetypePassword] = React.useState("");
    const [passwordReqs, setPasswordReqs] = React.useState(0);

    const navigate = useNavigate();

    //Login Password Progress bar
    const progressStyles = {
        background:
            passwordReqs === 1 ? 'red' :
                passwordReqs === 2 ? "rgb(250, 114, 25)" :
                    passwordReqs === 3 ? "yellow" : //orange
                        passwordReqs === 4 ? "lightGreen" :
                            "green"
    };
    const emptyStyles = {
        background: "rgb(127, 127, 127)"
    };

    // Password Requirements Counter for Progress Bar
    const passReqCounter = (pass, retypePass) => {
        props.onDataChange("Password", pass);

        let count = 0;
        // Check password requirements
        if (pass?.length >= 8) count++; // Minimum length
        if (/(?=.*\d)/.test(pass)) count++; // Contains a digit
        if (/(?=.*[A-Z])/.test(pass)) count++; // Contains an uppercase letter
        if (/(?=.*[a-z])/.test(pass)) count++; // Contains a lowercase letter

        // Check if passwords match
        if (pass === retypePass && pass.length > 0) count++; // Passwords match

        setPassword(pass || password); // Update password state
        setRetypePassword(retypePass || retypePassword); // Update retype password state
        setPasswordReqs(count); // Update password requirements count
    };

    React.useEffect(() => {
        const isPasswordValid = passwordReqs === 5 && password.length >= 8;
        const doPasswordsMatch = password === retypePassword && password.length > 0;

        setIsCreateDisabled(!(isPasswordValid && doPasswordsMatch));
    }, [password, retypePassword, passwordReqs]);

    let handleCreateAccount = async (dataItem) => {
        props.onStepSubmit()
    };

    return (
        /* Stepper */
        < Card style={{
            maxWidth: 650
        }}
            className="login-container signup-container" >

            <CardTitle className='card-title'>
                Sign Up
            </CardTitle>

            <CardBody>
                <Form onSubmit={handleCreateAccount} render={formRenderProps => <FormElement >
                    <fieldset className={'k-form-fieldset'}>
                        {/* Email */}
                        <FieldWrapper>
                            <div className='k-form-field-wrap'>
                                <Field onChange={(e) => props.onDataChange("UserEmail", e.value)} name={"email"} type={"email"} component={EmailInput} label={"Email"} validator={emailValidator} />
                            </div>
                        </FieldWrapper>

                        {/* First Name and Last Name */}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <FieldWrapper style={{ width: '46%' }}>
                                <Field onChange={(e) => props.onDataChange("FirstName", e.value)} name={'firstName'} component={FormInput} label={'First Name'} validator={firstNameValidator} />
                            </FieldWrapper>
                            <FieldWrapper style={{ width: '46%' }}>
                                <Field onChange={(e) => props.onDataChange("LastName", e.value)} name={'lastName'} component={FormInput} label={'Last Name'} validator={lastNameValidator} />
                            </FieldWrapper>
                        </div>

                        {/* Password */}
                        <FieldWrapper>
                            {isVisible1 ?
                                <span className="k-icon-xl k-font-icon pass-icon k-i-eye" onClick={() => setIsVisible1(!isVisible1)}></span> :
                                <span className="k-icon-xl k-font-icon pass-icon k-i-eye-slash" onClick={() => setIsVisible1(!isVisible1)}></span>
                            }
                            <Field
                                onChange={(e) => passReqCounter(e.value, retypePassword)}
                                autoComplete="new-password"
                                component={FormInput}
                                label={'Password'}
                                name={'password'}
                                type={isVisible1 ? 'text' : 'password'}
                                validator={requiredValidator}
                            />
                        </FieldWrapper>
                        {/* Retype Password */}
                        <FieldWrapper>
                            {isVisible2 ?
                                <span className="k-icon-xl k-font-icon pass-icon k-i-eye" onClick={() => setIsVisible2(!isVisible2)}></span> :
                                <span className="k-icon-xl k-font-icon pass-icon k-i-eye-slash" onClick={() => setIsVisible2(!isVisible2)}></span>
                            }
                            <Field
                                onChange={(e) => passReqCounter(password, e.value)}
                                component={FormInput}
                                label={'Retype Password'}
                                name={'retype-password'}
                                type={isVisible2 ? 'text' : 'password'}
                                validator={requiredValidator}
                            />
                        </FieldWrapper>

                        {/* Password Strength Protector */}
                        <FieldWrapper>
                            <div className=" k-form-field-wrap strength-bar">
                                <ChunkProgressBar value={passwordReqs} max={5} chunkCount={5} progressStyle={progressStyles} emptyStyle={emptyStyles} />
                            </div>

                            <div className="password-checklist">
                                <p style={{ color: password !== '' ? password.length >= 6 ? BIGreen : BIRed : BIGrey }}>
                                    {
                                        password.length >= 6
                                            ? <FaCheck className="icon-position" style={{ color: password.length >= 8 ? BIGreen : BIGrey }} />
                                            : <FaTimes className="icon-position" style={{ color: BIRed }} />
                                    } At least 8 characters
                                </p>

                                <p style={{ color: password !== '' ? password.match(/(?=.*\d)/) ? BIGreen : BIRed : BIGrey }}>
                                    {
                                        password.match(/(?=.*\d)/)
                                            ? <FaCheck className="icon-position" style={{ color: password.match(/(?=.*\d)/) ? BIGreen : BIGrey }} />
                                            : <FaTimes className="icon-position" style={{ color: BIRed }} />
                                    } At least 1 number
                                </p>

                                <p style={{ color: password !== '' ? password.match(/(?=.*[A-Z])/) ? BIGreen : BIRed : BIGrey }}>
                                    {
                                        password.match(/(?=.*[A-Z])/)
                                            ? <FaCheck className="icon-position" style={{ color: password.match(/(?=.*[A-Z])/) ? BIGreen : BIGrey }} />
                                            : <FaTimes className="icon-position" style={{ color: BIRed }} />
                                    } At least 1 capital letter
                                </p>

                                <p style={{ color: password !== '' ? password.match(/(?=.*[a-z])/) ? BIGreen : BIRed : BIGrey }}>
                                    {
                                        password.match(/(?=.*[a-z])/)
                                            ? <FaCheck className="icon-position" style={{ color: password.match(/(?=.*[a-z])/) ? BIGreen : BIGrey }} />
                                            : <FaTimes className="icon-position" style={{ color: BIRed }} />
                                    } At least 1 lowercase letter
                                </p>
                                <p style={{ color: password !== '' ? password == retypePassword ? BIGreen : BIRed : BIGrey }}>
                                    {
                                        password == retypePassword && password.length !== 0
                                            ? <FaCheck className="icon-position" style={{ color: password == retypePassword ? BIGreen : BIGrey }} />
                                            : <FaTimes className="icon-position" style={{ color: BIRed }} />
                                    } Passwords match
                                </p>
                            </div>
                        </FieldWrapper>

                        <span className={"k-form-separator"} style={{ margin: "30px 0px 10px 0px" }} />
                        <div style={{ display: "flex", justifyContent: 'space-between' }}>
                            <Button
                                themeColor={'secondary'}
                                style={{ marginTop: "25px", width: "45%", padding: "10px 4px 10px 4px" }}
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                // onClick={() => navigate('/')}
                                onClick={props.onPrevStepClick}
                            >
                                Back
                            </Button>
                            <Button
                                themeColor={'primary'}
                                type={'submit'}
                                style={{ marginTop: "25px", minWidth: "45%", padding: "10px 4px 10px 4px" }}
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                disabled={!formRenderProps.allowSubmit || isCreateDisabled}
                            >
                                Next
                            </Button>
                        </div>

                    </fieldset>
                </FormElement>} />
            </CardBody>
        </Card >

    );
}

